import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
  super.connectedCallback();
  this.#setupIntersectionObserver();

  const { signal } = this.#abortController;

  // Use document-level delegation since variant-picker
  // may not exist yet when connectedCallback runs
  document.addEventListener('variant:update', (e) => {
    this.#handleVariantUpdate(e);
  }, { signal });

  document.addEventListener('variant:selected', (e) => {
    this.#handleVariantSelected(e);
  }, { signal });

  document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
  document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

  this.#getInitialQuantity();
}

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }
  /**
 * Watches the main ATC button for text/state changes and mirrors to sticky button
 */
#observeMainButton() {
  const mainBtn = document.querySelector(
    `product-form-component[data-product-id="${this.dataset.productId}"] button[name="add"]`
  );
  if (!mainBtn) return;

  const syncFromMain = () => {
    const stickyBtn = this.refs.addToCartButton;
    if (!stickyBtn) return;

    // Mirror disabled state
    stickyBtn.disabled = mainBtn.disabled;

    // Get text from main button — handles nested span structure
    const mainText = mainBtn.querySelector('.add-to-cart-text__content')?.textContent?.trim();
    if (!mainText) return;

    // Write to sticky button text span
    const stickyTextSpan = stickyBtn.querySelector('.add-to-cart-text__content > span:first-child');
    if (stickyTextSpan) {
      stickyTextSpan.textContent = mainText;
    }

    // Also update variant-available so show/hide logic stays correct
    this.dataset.variantAvailable = String(!mainBtn.disabled);
  };

  // Run once immediately to sync initial state
  syncFromMain();

  // Watch for any DOM changes inside the main button
  const observer = new MutationObserver(syncFromMain);
  observer.observe(mainBtn, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['disabled']
  });

  // Clean up when component disconnects
  this.#abortController.signal.addEventListener('abort', () => observer.disconnect());
}
connectedCallback() {
  super.connectedCallback();
  this.#setupIntersectionObserver();

  const { signal } = this.#abortController;

  document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
  document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

  this.#getInitialQuantity();

  // Wait for DOM to be ready then start observing main button
  requestAnimationFrame(() => this.#observeMainButton());
}
  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 2000);

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.productImage) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
  if (event.detail.data.productId !== this.dataset.productId) return;

  const variant = event.detail.resource;

  const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
  if (!newStickyAddToCart) return;

  const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
  if (!newStickyBar) return;

  const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
  const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

  morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

  this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
  this.dataset.variantAvailable = variantAvailable;

  if (variant && variant.id) {
    this.dataset.currentVariantId = variant.id;
  }

  const productForm = this.#getProductForm();
  if (productForm) {
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  if (variant == null) {
    this.#handleVariantUnavailable();
    // Explicitly disable sticky button when no variant match
    this.refs.addToCartButton.disabled = true;
  } else {
    // Sync disabled state from the main button
    const mainBtn = this.#getProductForm()?.querySelector('button[name="add"]');
    if (mainBtn) {
      this.refs.addToCartButton.disabled = mainBtn.disabled;
    }
  }

  this.#updateButtonText();
};

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  /**
 * Updates the button text to include quantity and correct label
 */
#updateButtonText() {
  const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

  // Get the main button — source of truth
  const mainBtn = this.#getProductForm()?.querySelector('button[name="add"]');
  
  if (mainBtn) {
    // Sync disabled state from main button
    addToCartButton.disabled = mainBtn.disabled;

    // Get main button's text content from its inner span
    const mainTextSpan = mainBtn.querySelector('.add-to-cart-text__content > span:first-child > span');
    const mainText = mainTextSpan?.textContent?.trim() 
      ?? mainBtn.querySelector('.add-to-cart-text__content')?.textContent?.trim();

    // Mirror the text into sticky button
    const stickyTextSpan = addToCartButton.querySelector('.add-to-cart-text__content > span:first-child');
    if (stickyTextSpan && mainText) {
      stickyTextSpan.textContent = mainText;
    }
  }

  const available = !addToCartButton.disabled;
  quantityNumber.textContent = this.#currentQuantity.toString();
  quantityDisplay.style.display = (available && this.#currentQuantity > 1) ? 'inline' : 'none';
}
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
